import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuizStore } from "../state/store";
import type { Direction } from "../lib/types";

type WrongRow = {
  english: string;
  direction: Direction;
  totalWrong: number;
  totalCorrect: number;
  accuracy: number;
  state: string;
};

interface DonutSegment {
  key: string;
  label: string;
  count: number;
  color: string;
}

const DONUT_RADIUS = 40;
const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_RADIUS;

// Mirror src/index.css token values so the donut stays brand-consistent on
// both themes. (Tailwind utilities live in className; raw SVG strokes need
// the literal color.)
const TONE = {
  success: "rgb(16 185 129)",
  warning: "rgb(245 158 11)",
  danger: "rgb(239 68 68)",
  neutral: "rgb(71 85 105)",
  ring: "rgb(43 58 94)",
} as const;

export function StatsScreen() {
  const progress = useQuizStore((s) => s.progress);
  const counters = useQuizStore((s) => s.counters);
  const questionsAnswered = useQuizStore((s) => s.questionsAnswered);
  const markersSize = useQuizStore((s) => s.markers.size);
  const navigate = useNavigate();

  const vocab = useQuizStore((s) => s.vocab);

  const {
    topWrong,
    totalCorrect,
    totalWrong,
    enDeMastered,
    deEnMastered,
  } = useMemo(() => {
    let allCorrect = 0;
    let allWrong = 0;
    let enMastered = 0;
    let deMastered = 0;
    const wrongCandidates: WrongRow[] = [];

    for (const p of progress.values()) {
      allCorrect += p.totalCorrect;
      allWrong += p.totalWrong;
      if (p.state === "mastered") {
        if (p.direction === "en_de") enMastered++;
        else deMastered++;
      }
      if (p.totalWrong === 0) continue;
      const total = p.totalCorrect + p.totalWrong;
      wrongCandidates.push({
        english: p.english,
        direction: p.direction,
        totalWrong: p.totalWrong,
        totalCorrect: p.totalCorrect,
        accuracy: total === 0 ? 0 : Math.round((p.totalCorrect / total) * 100),
        state: p.state,
      });
    }
    wrongCandidates.sort(
      (a, b) => b.totalWrong - a.totalWrong || a.accuracy - b.accuracy || a.english.localeCompare(b.english),
    );

    return {
      topWrong: wrongCandidates.slice(0, 12),
      totalCorrect: allCorrect,
      totalWrong: allWrong,
      enDeMastered: enMastered,
      deEnMastered: deMastered,
    };
  }, [progress]);

  const globalAccuracy =
    totalCorrect + totalWrong === 0
      ? null
      : Math.round((totalCorrect / (totalCorrect + totalWrong)) * 100);

  const fresh = Math.max(
    0,
    counters.total - counters.mastered - counters.inProgress - counters.wrong,
  );

  const masteredPct =
    counters.total === 0 ? 0 : Math.round((counters.mastered / counters.total) * 1000) / 10;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="stats-screen">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold tracking-tight text-fg">Statistik</h2>
        <button
          type="button"
          onClick={() => navigate("/")}
          className="min-h-[44px] px-3 py-2 rounded-lg text-sm text-fg-muted hover:text-fg hover:bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-outline-strong"
          data-testid="back-to-quiz"
        >
          ← Zurück
        </button>
      </div>

      {/* Aggregate tiles — same as before */}
      <div className="grid grid-cols-3 gap-2">
        <Tile label="Fragen" value={String(questionsAnswered)} />
        <Tile label="Richtig" value={String(totalCorrect)} tone="success" />
        <Tile label="Falsch" value={String(totalWrong)} tone="danger" />
        <Tile
          label="Trefferquote"
          value={globalAccuracy == null ? "—" : `${globalAccuracy} %`}
          tone="accent"
          className="col-span-3"
        />
      </div>

      {/* NEW: Donut visualising the four progress states across all vocab.
       *   Replaces the per-letter list (was 26 mostly-zero rows). */}
      <section className="space-y-2">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-fg-muted">
          Lern-Fortschritt
        </h3>
        <ProgressDonut
          total={counters.total}
          mastered={counters.mastered}
          inProgress={counters.inProgress}
          wrong={counters.wrong}
          fresh={fresh}
          masteredPct={masteredPct}
        />
      </section>

      {/* Direction-Tiles (Issue #95): zeigt pro Lernrichtung wie viele
          Vokabeln mastered sind. Combined-Donut oben aggregiert beide
          Richtungen; hier brechen wir's auf für die Per-Richtung-Sicht. */}
      <section className="space-y-2">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-fg-muted">
          Pro Lernrichtung
        </h3>
        <div className="grid grid-cols-2 gap-2" data-testid="direction-tiles">
          <DirectionTile
            direction="en_de"
            label="EN → DE"
            mastered={enDeMastered}
            total={vocab.length}
          />
          <DirectionTile
            direction="de_en"
            label="DE → EN"
            mastered={deEnMastered}
            total={vocab.length}
          />
        </div>
      </section>

      {/* Markiert-Tile (Issue #123) — verlinkt zur Listen-Seite mit pre-filter. */}
      <section className="space-y-2">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-fg-muted">
          Markierte Vokabeln
        </h3>
        <Link
          to={markersSize > 0 ? "/words?filter=marked" : "/words"}
          data-testid="marked-tile"
          className="block bg-card rounded-xl border border-outline-soft hover:border-warning/40 transition-colors px-4 py-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-warning"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xl font-bold tabular text-warning">
                ★ {markersSize}{" "}
                <span className="text-xs font-normal text-fg-muted">
                  {markersSize === 1 ? "Vokabel" : "Vokabeln"}
                </span>
              </div>
              <div className="text-[11px] text-fg-faint mt-0.5">
                Listen-Seite öffnen →
              </div>
            </div>
            {markersSize === 0 && (
              <span className="shrink-0 text-xs text-fg-muted max-w-[55%] text-right">
                Tippe im Quiz auf ☆, um Wörter zum Nachüben zu sammeln.
              </span>
            )}
          </div>
        </Link>
      </section>

      {/* Häufig falsch — mit Direction pro Eintrag (Issue #95) */}
      <section className="space-y-2">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-fg-muted">
          Häufig falsch
        </h3>
        {topWrong.length === 0 ? (
          <p className="bg-card border border-outline-soft rounded-2xl p-4 text-sm text-fg-muted">
            Noch keine falschen Antworten — keep going.
          </p>
        ) : (
          <ol className="bg-card border border-outline-soft rounded-2xl divide-y divide-outline-soft overflow-hidden">
            {topWrong.map((row, idx) => (
              <li
                key={`${row.english}|${row.direction}`}
                className="px-3 py-2.5 flex items-center gap-3"
                data-testid={`wrong-row-${idx + 1}`}
              >
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-bg/40 text-xs font-mono text-fg-muted tabular">
                  {idx + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-fg truncate flex items-center gap-2">
                    <span>{row.english}</span>
                    <DirectionChip direction={row.direction} />
                  </div>
                  <div className="text-xs text-fg-muted tabular">
                    {row.totalWrong}× falsch · {row.totalCorrect}× richtig · {row.accuracy}%
                  </div>
                </div>
                <StatePill state={row.state} />
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}

interface ProgressDonutProps {
  total: number;
  mastered: number;
  inProgress: number;
  wrong: number;
  fresh: number;
  masteredPct: number;
}

/**
 * Four-segment donut showing the spread of all vocabs across the four
 * progress states (mastered / in_progress / wrong / fresh). Center cell
 * surfaces the most-important number: how many of the total have been
 * fully mastered. Hand-rolled SVG (no charts lib) — ~30 LOC.
 */
function ProgressDonut({
  total,
  mastered,
  inProgress,
  wrong,
  fresh,
  masteredPct,
}: ProgressDonutProps) {
  const segments: DonutSegment[] = [
    { key: "mastered", label: "Gemeistert", count: mastered, color: TONE.success },
    { key: "in_progress", label: "Läuft", count: inProgress, color: TONE.warning },
    { key: "wrong", label: "Falsch", count: wrong, color: TONE.danger },
    { key: "fresh", label: "Neu", count: fresh, color: TONE.ring },
  ];

  // Build dasharray + dashoffset per segment, walking around the circle.
  // SVG strokes start at 3 o'clock by default; rotate -90° so segment 1
  // starts at 12 o'clock (the conventional "top" of a donut).
  let acc = 0;
  const renderedSegments = segments.map((seg) => {
    const len = total === 0 ? 0 : (seg.count / total) * DONUT_CIRCUMFERENCE;
    const dashArray = `${len} ${DONUT_CIRCUMFERENCE - len}`;
    const dashOffset = -acc;
    acc += len;
    return { ...seg, dashArray, dashOffset };
  });

  return (
    <div
      className="bg-card border border-outline-soft rounded-2xl p-5"
      data-testid="progress-donut"
    >
      <div className="flex items-center justify-center gap-6 flex-wrap">
        <div className="relative w-40 h-40 shrink-0">
          <svg viewBox="0 0 100 100" className="w-full h-full">
            <circle
              cx="50"
              cy="50"
              r={DONUT_RADIUS}
              fill="transparent"
              stroke="rgb(31 44 74)"
              strokeWidth="14"
            />
            {renderedSegments.map((seg) => (
              <circle
                key={seg.key}
                cx="50"
                cy="50"
                r={DONUT_RADIUS}
                fill="transparent"
                stroke={seg.color}
                strokeWidth="14"
                strokeDasharray={seg.dashArray}
                strokeDashoffset={seg.dashOffset}
                transform="rotate(-90 50 50)"
                data-testid={`donut-segment-${seg.key}`}
              />
            ))}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
            <div className="text-2xl font-extrabold text-fg tabular leading-none">
              {mastered} / {total}
            </div>
            <div className="text-xs text-fg-muted mt-1 tabular">
              {masteredPct.toLocaleString("de-DE", { maximumFractionDigits: 1 })} % gemeistert
            </div>
          </div>
        </div>
        <ul className="flex flex-col gap-2 min-w-[180px] flex-1">
          {renderedSegments.map((seg) => (
            <li
              key={seg.key}
              className="flex items-center gap-2 text-sm text-fg-soft"
              data-testid={`donut-legend-${seg.key}`}
            >
              <span
                className="w-2.5 h-2.5 rounded-full shrink-0"
                style={{ background: seg.color }}
                aria-hidden
              />
              <span>{seg.label}</span>
              <span className="ml-auto text-xs text-fg-muted tabular">{seg.count}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

const TILE_TONE: Record<string, string> = {
  default: "text-fg",
  success: "text-success",
  danger: "text-danger",
  accent: "text-accent",
};

function Tile({
  label,
  value,
  tone = "default",
  className = "",
}: {
  label: string;
  value: string;
  tone?: keyof typeof TILE_TONE;
  className?: string;
}) {
  return (
    <div
      className={`bg-card rounded-xl px-3 py-2.5 border border-outline-soft text-center ${className}`}
    >
      <div className={`text-xl font-bold tabular ${TILE_TONE[tone]}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-fg-faint mt-0.5">{label}</div>
    </div>
  );
}

function DirectionTile({
  direction,
  label,
  mastered,
  total,
}: {
  direction: Direction;
  label: string;
  mastered: number;
  total: number;
}) {
  const isEnDe = direction === "en_de";
  const stripe = isEnDe ? "bg-accent" : "bg-warning";
  const labelClass = isEnDe ? "text-accent" : "text-warning";
  return (
    <div
      data-testid={`direction-tile-${direction}`}
      className="relative bg-card rounded-xl border border-outline-soft px-3 py-2.5"
    >
      <span
        aria-hidden
        className={`absolute top-2 left-0 w-1 rounded-r-full ${stripe}`}
        style={{ height: "calc(100% - 16px)" }}
      />
      <div className={`text-[10px] font-bold tracking-[0.12em] uppercase ${labelClass}`}>
        {label}
      </div>
      <div className="text-lg font-bold text-fg tabular mt-0.5">
        {mastered} / {total}
      </div>
      <div className="text-[11px] text-fg-muted">fertig</div>
    </div>
  );
}

function DirectionChip({ direction }: { direction: Direction }) {
  const isEnDe = direction === "en_de";
  const className = isEnDe
    ? "bg-accent-soft text-accent border-accent/30"
    : "bg-warning-soft text-warning border-warning/35";
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0 rounded border text-[9px] font-bold tracking-wider uppercase ${className}`}
    >
      {isEnDe ? "EN→DE" : "DE→EN"}
    </span>
  );
}

const STATE_PILL: Record<string, string> = {
  mastered: "bg-success-soft text-success",
  wrong: "bg-danger-soft text-danger",
  in_progress: "bg-warning-soft text-warning",
  fresh: "bg-bg/40 text-fg-muted",
};

function StatePill({ state }: { state: string }) {
  const label =
    state === "mastered"
      ? "Fertig"
      : state === "wrong"
        ? "Falsch"
        : state === "in_progress"
          ? "Läuft"
          : "Neu";
  return (
    <span
      className={`shrink-0 px-2 py-0.5 rounded-md text-[10px] uppercase tracking-wide font-semibold ${STATE_PILL[state] ?? STATE_PILL["fresh"]}`}
    >
      {label}
    </span>
  );
}
